import axios from 'axios';

// const API_URL = 'http://localhost:5000/api/auth';

const API_URL = process.env.REACT_APP_API_AUTH_URL;

const apiClient = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
});

// ─────────────────────────────────────────────
// Shared maintenance helpers
// ─────────────────────────────────────────────
const setMaintenance = () => {
    localStorage.setItem('maintenance', 'true');
    // Fire custom event so App.js reacts in the SAME tab
    // (the native 'storage' event only fires in OTHER tabs)
    window.dispatchEvent(new Event('maintenanceChange'));
};

const clearMaintenance = () => {
    if (localStorage.getItem('maintenance') === 'true') {
        console.log('✅ Auth: Maintenance OFF detected');
        localStorage.removeItem('maintenance');
        window.dispatchEvent(new Event('maintenanceChange'));
    }
};

// ─────────────────────────────────────────────
// Response interceptors
// ─────────────────────────────────────────────
apiClient.interceptors.response.use(
    (response) => {
        // Clear maintenance flag when backend responds normally again
        clearMaintenance();
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 503) {
            // Server is down for maintenance — set flag and notify App.js
            setMaintenance();
            // Return a never-resolving promise so the calling code freezes cleanly
            return new Promise(() => {});
        }
        return Promise.reject(error);
    }
);

export const authService = {

    // GET request
    getData: async (endpoint) => {
        try {
            const response = await apiClient.get(endpoint);
            return response.data;
        } catch (error) {
            console.error('Error fetching data:', error);
            throw error;
        }
    },

    // POST request
    postData: async (endpoint, data) => {
        try {
            const response = await apiClient.post(endpoint, data);
            return response.data;
        } catch (error) {
            console.error('Error posting data:', error);
            throw error;
        }
    },

    login: async (employee_id, password, recaptchaValue, ip) => {
        try {
            // ✅ Use apiClient (not raw axios) so the interceptors apply
            const response = await apiClient.post('/login', {
                employee_id,
                password,
                recaptchaValue,
                ipAddress: ip
            });

            if (response.data.accesstoken) {
                localStorage.setItem('user', JSON.stringify(response.data.accesstoken));
            }

            return response.data;
        } catch (error) {
            throw error.response?.data?.msg || 'An error occurred during login';
        }
    },

    logout: async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user'));
            // ✅ Use apiClient so a 503 during logout is also caught
            await apiClient.get('/logout', {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            localStorage.removeItem('user');
        } catch (error) {
            console.error('Logout error:', error);
        }
    },

    refreshToken: async () => {
        try {
            // ✅ Use apiClient
            const response = await apiClient.get('/refresh_token');
            if (response.data.accesstoken) {
                let user = JSON.parse(localStorage.getItem('user'));
                user = response.data.accesstoken;
                localStorage.setItem('user', JSON.stringify(user));
            }
            return response.data;
        } catch (error) {
            throw error.response?.data?.msg || 'Error refreshing token';
        }
    },

    getCurrentUser: async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user'));
            if (!token) return null;

            // ✅ Use apiClient — interceptor handles 503 here too
            const response = await apiClient.get('/infor', {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching user profile:', error.response?.data || error.message);
            return null;
        }
    },
};